import auth0 from "auth0-js";
import store from "@/store";
import { getAuthCallbackUrl } from "@/Shared/authUrls";
import { performLogout } from "@/Shared/logout";

const RENEW_BEFORE_EXPIRY_MS = 60 * 1000;
const RENEW_RETRY_MS = 15 * 1000;

const webAuth = new auth0.WebAuth({
  domain: process.env.VUE_APP_Auth_Domain,
  clientID: process.env.VUE_APP_Auth_Client_id,
});

type RenewableAuthResult = {
  accessToken?: string;
  idToken?: string;
  expiresIn?: number;
  refreshToken?: string;
};

let renewalTimeoutId: number | null = null;
let stopAuthWatch: (() => void) | null = null;
let renewalInProgress = false;

function clearRenewalTimeout(): void {
  if (renewalTimeoutId !== null) {
    window.clearTimeout(renewalTimeoutId);
    renewalTimeoutId = null;
  }
}

function getExpiryTime(): number {
  const issuedAt = Number(localStorage.getItem("tockentime"));
  const lifetimeSeconds = Number(localStorage.getItem("tokenExp"));

  if (
    !Number.isFinite(issuedAt) ||
    !Number.isFinite(lifetimeSeconds) ||
    issuedAt <= 0 ||
    lifetimeSeconds <= 0
  ) {
    return 0;
  }

  return issuedAt + lifetimeSeconds * 1000;
}

function scheduleRenewal(delay?: number): void {
  clearRenewalTimeout();

  if (!store.getters.authenticated) {
    return;
  }

  const expiresAt = getExpiryTime();
  if (!expiresAt) {
    return;
  }

  const renewalDelay =
    delay === undefined
      ? Math.max(0, expiresAt - Date.now() - RENEW_BEFORE_EXPIRY_MS)
      : Math.max(0, delay);

  renewalTimeoutId = window.setTimeout(() => {
    void renewSession();
  }, renewalDelay);
}

async function renewSession(): Promise<void> {
  if (renewalInProgress || !store.getters.authenticated) {
    return;
  }

  renewalInProgress = true;

  try {
    const authResult = await new Promise<RenewableAuthResult>((resolve, reject) => {
      webAuth.checkSession(
        {
          redirectUri: getAuthCallbackUrl(),
          responseType: "token id_token",
          scope: "openid profile email",
        },
        (error, result) => {
          if (error) {
            reject(error);
            return;
          }

          resolve(result || {});
        }
      );
    });

    if (!authResult?.accessToken || !authResult?.idToken || !authResult?.expiresIn) {
      throw new Error("Auth0 silent renewal returned an incomplete session.");
    }

    await store.dispatch("loginuser", authResult);
    console.info("Authentication session renewed.");
    scheduleRenewal();
  } catch (error) {
    console.warn("Unable to renew the authentication session.", error);

    const remainingTime = getExpiryTime() - Date.now();
    if (remainingTime > 0) {
      scheduleRenewal(Math.min(RENEW_RETRY_MS, remainingTime));
    } else {
      await performLogout({ audit: false });
    }
  } finally {
    renewalInProgress = false;
  }
}

export function startTokenRenewal(): void {
  if (stopAuthWatch) {
    return;
  }

  stopAuthWatch = store.watch(
    (_state, getters) => getters.authenticated,
    (authenticated) => {
      if (authenticated) {
        scheduleRenewal();
      } else {
        clearRenewalTimeout();
      }
    }
  );

  scheduleRenewal();
}

export function stopTokenRenewal(): void {
  clearRenewalTimeout();

  if (stopAuthWatch) {
    stopAuthWatch();
    stopAuthWatch = null;
  }
}
